import { useEffect, useRef, useState } from 'react';
import {
  Alert, Button, CircularProgress, ListItemText, Menu, MenuItem, Snackbar, Tooltip,
} from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import type { Map as MapLibreMap } from 'maplibre-gl';

import { api } from '../api/client';
import type { DirectionsLeg, PlanRequest, RoutePlan } from '../api/types';
import {
  OFFERED_EXPORT_FORMATS, exportRoute, type ExportDay, type ExportFormat,
} from '../export/routeExport';
import { captureRouteImages } from '../export/mapCapture';

/** One planned day the caller is offering for export. */
export interface ExportableDay {
  /** "Day 2 — Sat 10 Oct". Empty for a route that is not part of an operation. */
  label: string;
  /** YYYY-MM-DD, so the exported schedule can carry real timestamps. */
  date?: string;
  /** The request this day was planned from, so the turns follow its line. */
  request: PlanRequest;
  plan: RoutePlan;
}

/**
 * Takes a planned route — or a whole operation — away with you.
 *
 * Offered in four places from one component: beside a plan on the planner,
 * beside a saved route, beside a planned operation, and beside a saved one.
 * All four hold the same two things per day, the request it was made from and
 * the plan that came back, so none of them needs to know anything about files.
 *
 * The turns are fetched here rather than arriving with the plan. They are
 * several kilobytes of prose that would otherwise ride on every re-plan, every
 * drag of a stop and every saved route, to be read on the day somebody asks
 * for a file. See PlanDirections on the server.
 */
export function ExportRouteButton({
  days, name, getMap, onShowDay, activeDay = 0, size = 'small',
}: {
  /** Every day to put in the file, in order. One entry for a single route. */
  days: ExportableDay[];
  /** What to call the route or operation, and the file. */
  name: string;
  /**
   * The map on the page, for the PDF's plates.
   *
   * A function rather than the map itself, because the map arrives after the
   * first render and this would otherwise be stale. Omitted, or returning
   * null, simply means a PDF without pictures.
   */
  getMap?: () => MapLibreMap | null;
  /**
   * Puts one day on the map, so its picture can be taken.
   *
   * The map draws a day at a time, so an operation's plates can only be taken
   * by walking it through them. Without this an operation still exports — it
   * just carries the pictures of whichever day was already showing, which is
   * worse than none, so only that day gets any.
   */
  onShowDay?: (index: number) => void;
  /** The day showing now, to be put back when the pictures are taken. */
  activeDay?: number;
  size?: 'small' | 'medium';
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [busy, setBusy] = useState<ExportFormat | null>(null);
  const [note, setNote] = useState<string | null>(null);

  /*
   * Fetched once, then reused.
   *
   * Somebody taking a PDF and a GPX of the same route is asking the same
   * question twice, and the second answer costs a router call per day and a
   * dozen camera moves.
   */
  const built = useRef<ExportDay[] | null>(null);
  const plated = useRef(false);

  /*
   * Thrown away the moment the route changes, and not before.
   *
   * This used to rely on the component being remade with the plan, which is
   * not what happens: on the saved list the button holds its place in the
   * tree while the route beside it is swapped, so exporting one route and
   * then another handed the second route's stops the first route's turns and
   * maps.
   *
   * Keyed on what the days ARE rather than on the array holding them. Two of
   * the four callers build that array inline, so its identity changes on
   * every render — which would empty the cache constantly, and could empty it
   * underneath an export that was halfway through fetching.
   */
  const signature = days
    .map((d) => `${d.plan.generatedAt}@${d.plan.stops.length}@${d.plan.totals.distanceMeters}`)
    .join('|');
  useEffect(() => {
    built.current = null;
    plated.current = false;
  }, [signature]);

  /** Lets React commit and the map take the new day before it is shot. */
  const settle = () => new Promise<void>((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => setTimeout(resolve, 120)));
  });

  const run = async (format: ExportFormat) => {
    setAnchor(null);
    setBusy(format);
    try {
      /*
        Held locally for the rest of this export.

        The ref is the cache between exports; the array is what THIS one is
        writing. Reading the ref again after every await would let a render
        that cleared it halfway through hand the writer a null.
      */
      let sections = built.current;
      if (!sections) {
        sections = [];
        for (const day of days) {
          let directions: DirectionsLeg[];
          try {
             
            directions = await api.directions(
              day.request,
              day.plan.stops.map((s) => ({ lat: s.lat, lng: s.lng })),
              day.plan.avoidedZones.map((z) => z.polygon),
            );
          } catch {
            /*
              A file without the turns beats no file.

              The stops, the times and the drawn line are all here already;
              the narrative is the one part that needs the router. Left
              uncached so the next attempt tries again.
            */
            directions = [];
            setNote('Saved without turn-by-turn — the routing engine did not answer.');
          }
          sections.push({
            label: day.label,
            plan: day.plan,
            directions,
            ...(day.date ? { date: day.date } : {}),
          });
        }
        built.current = sections;
      }

      /*
        Pictures only for the PDF, and only if there is a map to borrow.

        Every other format here is text, and a base64 PNG inside a GPX would
        be bytes no reader would look at.
      */
      if (format === 'pdf' && !plated.current) {
        const map = getMap?.() ?? null;
        if (map) {
          plated.current = true;
          try {
            for (let i = 0; i < days.length; i += 1) {
              if (days.length > 1 && onShowDay) {
                onShowDay(i);
                 
                await settle();
              } else if (days.length > 1) {
                // No way to change the day, so only the one showing is shot.
                if (i !== activeDay) continue;
              }
               
              const section = sections[i];
              const day = days[i];
              if (section && day) section.images = await captureRouteImages(map, day.plan);
            }
          } catch {
            plated.current = false;
            setNote('Saved without the map images — the map could not be read.');
          } finally {
            if (days.length > 1 && onShowDay) onShowDay(activeDay);
          }
        }
      }

      await exportRoute(format, { name, days: sections });
    } catch (err) {
      setNote((err as Error).message || 'That export could not be written.');
    } finally {
      setBusy(null);
    }
  };

  if (days.length === 0) return null;

  /*
    One format is not a menu.

    While only the PDF is offered, a list of one is a click asking the same
    question twice. The button writes it directly instead, and goes back to
    opening a menu the moment a second format is offered again — nothing
    here has to be remembered and changed back.
  */
  const only = OFFERED_EXPORT_FORMATS.length === 1
    ? OFFERED_EXPORT_FORMATS[0]
    : undefined;

  /*
    And the label stays one word.

    The toolbar it sits in is kept to a single line at panel width, closely
    enough that "Export PDF" pushed Save off the end of it — a row that
    reads at a glance is worth more than naming the format in it. The format
    is said on hover instead, where there is room for the whole sentence.
  */
  const button = (
    <Button
      size={size}
      color="inherit"
      startIcon={busy
        ? <CircularProgress size={16} color="inherit" />
        : <FileDownloadOutlinedIcon />}
      disabled={busy !== null}
      onClick={(e) => { if (only) void run(only.key); else setAnchor(e.currentTarget); }}
      {...(only ? {} : { 'aria-haspopup': 'menu' as const })}
    >
      Export
    </Button>
  );

  return (
    <>
      {only
        ? <Tooltip title={`${only.label} — ${only.hint.toLowerCase()}`}>{button}</Tooltip>
        : button}

      {!only && (
        <Menu anchorEl={anchor} open={anchor !== null} onClose={() => setAnchor(null)}>
          {OFFERED_EXPORT_FORMATS.map((f) => (
            <MenuItem key={f.key} onClick={() => void run(f.key)}>
              <ListItemText primary={f.label} secondary={f.hint} />
            </MenuItem>
          ))}
        </Menu>
      )}

      <Snackbar
        open={note !== null}
        autoHideDuration={6000}
        onClose={() => setNote(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="info" variant="filled" onClose={() => setNote(null)}>
          {note}
        </Alert>
      </Snackbar>
    </>
  );
}
